/**
 * Built-in Dynamic List Resolvers
 *
 * Registers portal-side resolvers for `x-ms-dynamic-list` operationIds that
 * are not part of any connector swagger. Imported for side-effects from the
 * dynamic-data plumbing so resolvers are available at first use.
 */

import {
  registerBuiltinDynamicList,
  type BuiltinDynamicListResolver,
} from './builtinDynamicListRegistry';
import {
  listAllFoundryAgentsBoth,
  listFoundryAgentVersions,
  type FoundryAgent,
} from './foundryAgentService';

// =============================================================================
// FOUNDRY AGENT LIST
// =============================================================================

/**
 * Extract the resolved Foundry project endpoint from a connection reference.
 *
 * Returns undefined when the connection stores a legacy account-level endpoint
 * that still requires ARM-based project discovery. Phase A keeps this simple;
 * Phase B can hook up `useResolvedFoundryEndpoint` semantics behind a non-React
 * helper if/when needed.
 */
function getFoundryEndpointFromConnection(
  props: Record<string, unknown> | undefined
): string | undefined {
  if (!props) return undefined;
  const endpoint = props['endpoint'];
  if (typeof endpoint !== 'string' || endpoint.length === 0) return undefined;
  return endpoint;
}

const foundryAgentVersionLabel = (agent: FoundryAgent): string =>
  agent.agentVersion === 'v2' ? 'v2' : 'v1';

const listFoundryAgentsResolver: BuiltinDynamicListResolver = async (ctx) => {
  const projectEndpoint = getFoundryEndpointFromConnection(
    ctx.connectionReference?.connectionProperties
  );
  if (!projectEndpoint) {
    return {
      options: [],
      error:
        'No Foundry project endpoint configured. Connect an AI Foundry project to load agents.',
    };
  }

  let token: string | null = null;
  try {
    token = await ctx.getFoundryToken();
  } catch {
    return {
      options: [],
      error: 'Authentication required for Foundry.',
      isAuthError: true,
    };
  }
  if (!token) {
    return {
      options: [],
      error: 'Authentication required for Foundry.',
      isAuthError: true,
    };
  }

  try {
    const agents = await listAllFoundryAgentsBoth(projectEndpoint, token);
    return {
      options: agents.map((agent) => {
        // v1 assistants identify by assistant ID, v2 agents identify by name.
        const identifier =
          agent.agentVersion === 'v1' ? agent.id : (agent.name ?? agent.id);
        const modelType =
          agent.agentVersion === 'v2' ? 'FoundryAgentServiceV2' : 'FoundryAgentService';
        return {
          value: identifier,
          label: agent.name ?? agent.id,
          description: `${agent.model} · ${foundryAgentVersionLabel(agent)}`,
          data: {
            id: agent.id,
            name: agent.name,
            model: agent.model,
            instructions: agent.instructions ?? '',
            agentVersion: agent.agentVersion,
            // Derived fields consumed by `x-ms-on-select` mappings:
            agentModelType: modelType,
            foundryVersionName: agent.agentVersion === 'v2' ? (agent.version ?? '') : '',
          },
        };
      }),
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const isAuth = /auth(entic|oriz)/i.test(message) || /401|403/.test(message);
    return {
      options: [],
      error: message,
      ...(isAuth ? { isAuthError: true } : {}),
    };
  }
};

registerBuiltinDynamicList('listFoundryAgents', listFoundryAgentsResolver);

const listFoundryAgentVersionsResolver: BuiltinDynamicListResolver = async (ctx) => {
  const projectEndpoint = getFoundryEndpointFromConnection(
    ctx.connectionReference?.connectionProperties
  );
  const agentId = ctx.parameterValues['foundryAgentId'];
  const agentModelType = ctx.parameterValues['agentModelType'];
  if (
    agentModelType !== 'FoundryAgentServiceV2' ||
    !projectEndpoint ||
    typeof agentId !== 'string' ||
    !agentId
  ) {
    return { options: [] };
  }

  let token: string | null = null;
  try {
    token = await ctx.getFoundryToken();
  } catch {
    return {
      options: [],
      error: 'Authentication required for Foundry.',
      isAuthError: true,
    };
  }
  if (!token) {
    return {
      options: [],
      error: 'Authentication required for Foundry.',
      isAuthError: true,
    };
  }

  try {
    const versions = await listFoundryAgentVersions(projectEndpoint, agentId, token);
    return {
      options: versions.map((version) => ({
        value: version.version,
        label: `Version ${version.version}`,
        data: {
          version: version.version,
          model: version.definition.model,
          instructions: version.definition.instructions ?? '',
        },
      })),
    };
  } catch (err) {
    return {
      options: [],
      error: err instanceof Error ? err.message : String(err),
    };
  }
};

registerBuiltinDynamicList('listFoundryAgentVersions', listFoundryAgentVersionsResolver);
